import * as React from 'react';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';

// COMPONENTS
import Button from '../../components/Button';
import DateTimeInput from '../../components/DateTimeInput';
import ScrollToTopButton from '../../components/ScrollToTopButton';
import SingleSelect from '../../components/SingleSelect';
import FileUpload from '../../components/FileUpload';
import FilesList from '../../components/FilesList';

// TYPES
import { IncidentFormTypes } from './IncidentSubmit';

interface StepOneTypes {
	incidentFormData: IncidentFormTypes;
	setIncidentFormData: React.Dispatch<React.SetStateAction<IncidentFormTypes>>;
	setActiveStep: React.Dispatch<React.SetStateAction<string>>;
}

const StepOne: React.FC<StepOneTypes> = ({ incidentFormData, setIncidentFormData, setActiveStep }) => {
	// Get all incident Types from Redux store
	const incidentArchiveTypes = useAppSelector((state) => state.lists.incidentArchiveTypes);

	const onSubmitStepOne = (values: any) => {
		setActiveStep('step2');

		window.scrollTo(0, 0);

		setIncidentFormData((prevState) => ({
			...prevState,

			title: values.titleInput,
			occurrence: values.occurrenceInput,
			type: values.typeInput,
			location: values.locationInput,
			impacted: values.impactedInput,
			wasWitnessedFirstHand: values.wasWitnessedFirstHandInput,
			wasAuthorityAlerted: values.wasAuthorityAlertedInput,
			notifiedAuthorities: values.notifiedAuthoritiesInput,
			notified: values.notifiedInput,
			description: values.descriptionInput,
			actions: values.actionsInput,

			newAttachments: values.newAttachments,
			createAttachments: values.createAttachments,

			attachments: values.attachments,
			deleteAttachmentIds: values.deleteAttachmentIds
		}));
	};

	const validationSchemaStepOne = Yup.object({
		titleInput: Yup.string().required('Required'),
		occurrenceInput: Yup.string().required('Required'),
		typeInput: Yup.string().required('Required'),
		locationInput: Yup.string().required('Required'),
		impactedInput: Yup.string().required('Required'),
		wasWitnessedFirstHandInput: Yup.boolean().required('Required'),
		wasAuthorityAlertedInput: Yup.boolean().required('Required'),
		notifiedAuthoritiesInput: Yup.string().when('wasAuthorityAlertedInput', {
			is: true,
			then: Yup.string().required('Required')
		}),
		descriptionInput: Yup.string().required('Required'),
		actionsInput: Yup.string().required('Required')
	});

	const initialValuesStepOne = {
		titleInput: incidentFormData.title || '',
		occurrenceInput: incidentFormData.occurrence || '',
		typeInput: incidentFormData.type || '',
		locationInput: incidentFormData.location || '',
		impactedInput: incidentFormData.impacted || '',
		wasWitnessedFirstHandInput: incidentFormData.wasWitnessedFirstHand || false,
		wasAuthorityAlertedInput: incidentFormData.wasAuthorityAlerted || false,
		notifiedAuthoritiesInput: incidentFormData.notifiedAuthorities || '',
		notifiedInput: incidentFormData.notified || '',
		descriptionInput: incidentFormData.description || '',
		actionsInput: incidentFormData.actions || '',
		newAttachments: incidentFormData.newAttachments || [],
		createAttachments: incidentFormData.createAttachments || [],

		attachments: incidentFormData.attachments || [],
		deleteAttachmentIds: incidentFormData.deleteAttachmentIds || []
	};

	return (
		<Formik
			initialValues={initialValuesStepOne}
			validationSchema={validationSchemaStepOne}
			onSubmit={onSubmitStepOne}
			enableReinitialize
		>
			{(formik) => {
				return (
					<Form translate="yes" className="general-form">
						<div className="general-form-layout-wrapper">
							<div className="form-main">
								<div className="form-group">
									<label htmlFor="titleInput" className="label">
										Subject
									</label>

									<div className="form-field">
										<Field
											name="titleInput"
											id="titleInput"
											placeholder={'e.g. Head injury at football match'}
											type="text"
											className="form-input"
										/>

										<ErrorMessage name="titleInput" component="div" className="error-message" />
									</div>
								</div>

								<div className="form-group">
									<label className="label">Date and time of incident</label>

									<DateTimeInput
										dateTimeString={formik.values.occurrenceInput}
										onChange={(val: string) => formik.setFieldValue('occurrenceInput', val)}
									/>
								</div>

								<div className="form-group">
									<label htmlFor="typeInput" className="label">
										Incident Type
									</label>

									<SingleSelect
										options={incidentArchiveTypes}
										name="typeInput"
										value={formik.values.typeInput}
										onChange={(val: string) => formik.setFieldValue('typeInput', val)}
									/>

									<ErrorMessage name="typeInput" component="div" className="error-message" />
								</div>

								<div className="form-group">
									<label htmlFor="locationInput" className="label">
										Location of the incident
									</label>

									<div className="form-field">
										<Field name="locationInput" id="locationInput" type="text" className="form-input" />

										<ErrorMessage name="locationInput" component="div" className="error-message" />
									</div>
								</div>

								<div className="form-group">
									<label htmlFor="impactedInput" className="label">
										Name of person(s) impacted
									</label>

									<div className="form-field">
										<Field name="impactedInput" id="impactedInput" type="text" className="form-input" />

										<ErrorMessage name="impactedInput" component="div" className="error-message" />
									</div>
								</div>

								<div className="form-group">
									<label className="label">Did you witness the incident first hand?</label>

									<div className="form-field">
										<div className="radio-row">
											<Field
												name="wasWitnessedFirstHandInput"
												id="wasWitnessedFirstHandInput"
												type="radio"
												value={true}
												checked={formik.values.wasWitnessedFirstHandInput === true}
												onChange={() => formik.setFieldValue('wasWitnessedFirstHandInput', true)}
											/>

											<span className="radio-option">Yes</span>
										</div>

										<div className="radio-row">
											<Field
												name="wasWitnessedFirstHandInput"
												id="wasWitnessedFirstHandInput"
												type="radio"
												value={false}
												checked={formik.values.wasWitnessedFirstHandInput === false}
												onChange={() => formik.setFieldValue('wasWitnessedFirstHandInput', false)}
											/>

											<span className="radio-option">No</span>
										</div>

										<ErrorMessage name="wasWitnessedFirstHandInput" component="div" className="error-message" />
									</div>
								</div>

								<div className="form-group">
									<label className="label">Was an authority notified?</label>

									<div className="form-field">
										<div className="radio-row">
											<Field
												name="wasAuthorityAlertedInput"
												id="wasAuthorityAlertedInput"
												type="radio"
												value={true}
												checked={formik.values.wasAuthorityAlertedInput === true}
												onChange={() => formik.setFieldValue('wasAuthorityAlertedInput', true)}
											/>

											<span className="radio-option">Yes</span>
										</div>

										<div className="radio-row">
											<Field
												name="wasAuthorityAlertedInput"
												id="wasAuthorityAlertedInput"
												type="radio"
												value={false}
												checked={formik.values.wasAuthorityAlertedInput === false}
												onChange={() => formik.setFieldValue('wasAuthorityAlertedInput', false)}
											/>

											<span className="radio-option">No</span>
										</div>

										<ErrorMessage name="wasAuthorityAlertedInput" component="div" className="error-message" />
									</div>
								</div>

								{formik.values.wasAuthorityAlertedInput === true && (
									<div className="form-group">
										<label htmlFor="notifiedAuthoritiesInput" className="label">
											If so, please provide the name of the authority and the contact details of the person that you
											spoke to at that authority
										</label>

										<div className="form-field">
											<Field
												as="textarea"
												name="notifiedAuthoritiesInput"
												id="notifiedAuthoritiesInput"
												type="text"
												className="form-input"
											/>

											<ErrorMessage name="notifiedAuthoritiesInput" component="div" className="error-message" />
										</div>
									</div>
								)}

								<div className="form-group">
									<label className="label">Date and time of notification</label>

									<DateTimeInput
										dateTimeString={formik.values.notifiedInput}
										onChange={(val: string) => formik.setFieldValue('notifiedInput', val)}
									/>
								</div>

								<div className="form-group">
									<label htmlFor="descriptionInput" className="label">
										Description of the incident
									</label>

									<div className="form-field">
										<Field
											as="textarea"
											name="descriptionInput"
											id="descriptionInput"
											type="text"
											className="form-input"
										/>

										<ErrorMessage name="descriptionInput" component="div" className="error-message" />
									</div>
								</div>

								<div className="form-group">
									<label htmlFor="actionsInput" className="label">
										What actions were taken to respond to this incident?
									</label>

									<div className="form-field">
										<Field as="textarea" name="actionsInput" id="actionsInput" type="text" className="form-input" />

										<ErrorMessage name="actionsInput" component="div" className="error-message" />
									</div>
								</div>

								<div className="form-group">
									<label className="label">Add any supporting documents or images</label>

									<FilesList
										files={
											(incidentFormData.attachments &&
												incidentFormData.attachments.filter(
													(item) => !incidentFormData.deleteAttachmentIds?.includes(item.id || '')
												)) ||
											[]
										}
										onChange={(vals: string[]) => formik.setFieldValue('deleteAttachmentIds', vals)}
										multiple={true}
									/>

									<FileUpload
										name="createAttachments"
										onChange={(vals: string[], files: any[]) => {
											formik.setFieldValue('createAttachments', vals);
											formik.setFieldValue('newAttachments', files);
										}}
										files={incidentFormData.newAttachments}
										multiple={true}
										maxSizeMb={5}
									></FileUpload>
								</div>
							</div>
						</div>

						<ScrollToTopButton />

						<div className="general-divider"></div>

						<div className="form-button-group">
							<Button type="submit" className="btn-primary submit-btn complaints-submit-page">
								Continue
							</Button>
						</div>
					</Form>
				);
			}}
		</Formik>
	);
};

export default StepOne;
